<template>
	<div class="press-carousel" role="region" aria-roledescription="carousel" aria-label="Press coverage of Paydeck">
		<div ref="track" class="press-track" tabindex="0" @scroll.passive="onScroll">
			<a
				v-for="(item, i) in slides"
				:key="item.href"
				:class="['press-slide', `is-${item.size}`, `tone-${item.tone}`]"
				:href="item.href"
				target="_blank"
				rel="noopener noreferrer"
				:aria-label="`${item.outlet}: ${item.headline} Read the article (opens in a new tab)`"
			>
				<span class="press-slide-top">
					<span class="press-slide-outlet">{{ item.outlet }}</span>
					<span class="press-slide-index pd-num">{{ String(i + 1).padStart(2, "0") }} / {{ String(press.length).padStart(2, "0") }}</span>
				</span>
				<Quote v-if="item.size === 'tall'" class="press-slide-mark" :size="44" :stroke-width="1.5" aria-hidden="true" />
				<span class="press-slide-quote">“{{ item.headline }}”</span>
				<span class="press-slide-read">Read on {{ item.outlet }} <ArrowUpRight :size="16" :stroke-width="2.4" aria-hidden="true" /></span>
			</a>
			<router-link to="/about-us" class="press-slide is-tall tone-black press-slide-end">
				<span class="press-slide-outlet">The full story</span>
				<span class="press-slide-quote">Seven publications. One idea: card payments, wherever life needs them.</span>
				<span class="press-slide-read">Read our story <ArrowUpRight :size="16" :stroke-width="2.4" aria-hidden="true" /></span>
			</router-link>
		</div>
		<div class="press-controls">
			<div class="press-progress" aria-hidden="true"><i :style="{ width: `${progress * 100}%` }" /></div>
			<div class="press-arrows">
				<PdIconButton :icon="ArrowLeft" label="Previous stories" variant="neutral" :disabled="atStart" @click="step(-1)" />
				<PdIconButton :icon="ArrowRight" label="Next stories" variant="ink" :disabled="atEnd" @click="step(1)" />
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { ArrowLeft, ArrowRight, ArrowUpRight, Quote } from "lucide-vue-next";
import PdIconButton from "@paydeck-ui/components/PdIconButton.vue";
import { press } from "@/content/about";

// Bento rhythm: tall tiles fill both rows, short tiles stack in pairs.
// Talls are orange and shorts checker black and gray, so no two touching tiles share a colour.
const layout = [
	{ size: "tall", tone: "orange" },
	{ size: "short", tone: "black" },
	{ size: "short", tone: "gray" },
	{ size: "tall", tone: "orange" },
	{ size: "short", tone: "gray" },
	{ size: "short", tone: "black" },
	{ size: "tall", tone: "orange" }
];
const slides = computed(() => press.map((item, i) => ({ ...item, ...layout[i % layout.length] })));

const track = ref(null);
const progress = ref(0);
const atStart = ref(true);
const atEnd = ref(false);

function onScroll() {
	const el = track.value;
	const max = el.scrollWidth - el.clientWidth;
	progress.value = max > 0 ? Math.min(1, Math.max(0.08, el.scrollLeft / max)) : 1;
	atStart.value = el.scrollLeft <= 4;
	atEnd.value = el.scrollLeft >= max - 4;
}

function step(direction) {
	const el = track.value;
	el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
}

onMounted(onScroll);
</script>
